import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { isGoldChannel } from './channel_utils';
import { loadCountriesData, buildCountryIndex } from '../../../utils/countries';

const WORLD_GEOJSON_URL = 'https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json';

const NO_CHANNEL_FILL = '#eef2f6';
const COLOR_MIN = { r: 198, g: 219, b: 239 };
const COLOR_MAX = { r: 13, g: 84, b: 143 };

function normalizeCountryKey(value) {
  return String(value || '').trim().toLowerCase();
}

function resolveCountryCode(country, index) {
  if (!country || !index) return null;
  const raw = String(country).trim();
  if (!raw) return null;

  const upper = raw.toUpperCase();
  if (index.byCca3[upper]) return index.byCca3[upper].cca3;

  return index.byName[normalizeCountryKey(raw)] || null;
}

function getCountryFillColor(count, maxCount) {
  if (!count || count <= 0) return NO_CHANNEL_FILL;
  if (maxCount <= 1) return `rgb(${COLOR_MAX.r}, ${COLOR_MAX.g}, ${COLOR_MAX.b})`;

  const t = Math.min(1, count / maxCount);
  const r = Math.round(COLOR_MIN.r + (COLOR_MAX.r - COLOR_MIN.r) * t);
  const g = Math.round(COLOR_MIN.g + (COLOR_MAX.g - COLOR_MIN.g) * t);
  const b = Math.round(COLOR_MIN.b + (COLOR_MAX.b - COLOR_MIN.b) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

function buildChannelStats(channels, countryIndex) {
  const counts = {};
  const goldCounts = {};
  const channelsByCca3 = {};
  const unlocated = [];

  channels.forEach((channel) => {
    const cca3 = resolveCountryCode(channel.country, countryIndex);
    if (!cca3) {
      unlocated.push(channel);
      return;
    }

    counts[cca3] = (counts[cca3] || 0) + 1;
    if (isGoldChannel(channel)) {
      goldCounts[cca3] = (goldCounts[cca3] || 0) + 1;
    }
    if (!channelsByCca3[cca3]) channelsByCca3[cca3] = [];
    channelsByCca3[cca3].push(channel);
  });

  const maxCount = Object.values(counts).reduce((max, value) => Math.max(max, value), 0);
  return { counts, goldCounts, channelsByCca3, unlocated, maxCount };
}

function buildCountryPopupContent(countryName, count, goldCount, list, showGold, onChannelClick) {
  const container = document.createElement('div');
  container.className = 'channel-map-country-popup';

  const title = document.createElement('strong');
  title.textContent = countryName;
  container.appendChild(title);

  const subtitle = document.createElement('div');
  subtitle.className = 'text-muted small';
  subtitle.textContent = count === 1 ? '1 canal' : `${count} canales`;
  container.appendChild(subtitle);

  if (showGold && goldCount > 0) {
    const goldLine = document.createElement('div');
    goldLine.className = 'channel-map-country-popup__gold small';
    goldLine.innerHTML = `<span class="channel-gold-medal channel-gold-medal--inline me-1" aria-hidden="true"></span>${goldCount} GOLD`;
    container.appendChild(goldLine);
  }

  if (list.length > 0) {
    const listEl = document.createElement('ul');
    listEl.className = 'channel-map-country-popup__list list-unstyled small mb-0 mt-2';

    list.forEach((channel) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn btn-link btn-sm p-0 text-start channel-map-country-popup__link';

      if (showGold && isGoldChannel(channel)) {
        const medal = document.createElement('span');
        medal.className = 'channel-gold-medal channel-gold-medal--inline me-1';
        medal.setAttribute('aria-hidden', 'true');
        button.appendChild(medal);
      }

      button.appendChild(document.createTextNode(channel.name || 'Sin nombre'));
      button.addEventListener('click', () => onChannelClick?.(channel.id));
      item.appendChild(button);
      listEl.appendChild(item);
    });

    container.appendChild(listEl);
  }

  return container;
}

function CountriesGeoJsonLayer({
  geoJson,
  channelCounts,
  goldCounts,
  channelsByCca3,
  maxCount,
  showGold,
  onChannelClick,
}) {
  const geoJsonRef = useRef(null);
  const countsKey = useMemo(() => JSON.stringify(channelCounts), [channelCounts]);

  const style = useCallback(
    (feature) => {
      const cca3 = feature.id;
      const count = channelCounts[cca3] || 0;
      return {
        fillColor: getCountryFillColor(count, maxCount),
        weight: 1,
        opacity: 1,
        color: '#ffffff',
        fillOpacity: count > 0 ? 0.95 : 0.75,
      };
    },
    [channelCounts, maxCount]
  );

  const onEachFeature = useCallback(
    (feature, layer) => {
      const cca3 = feature.id;
      const count = channelCounts[cca3] || 0;
      const goldCount = goldCounts[cca3] || 0;
      const countryName = feature.properties?.name || cca3;
      const list = channelsByCca3[cca3] || [];

      if (count > 0) {
        layer.bindPopup(
          buildCountryPopupContent(countryName, count, goldCount, list, showGold, onChannelClick)
        );
        const label = showGold && goldCount > 0
          ? `${count} <span class="channel-map-country-label__gold">★${goldCount}</span>`
          : `${count}`;
        layer.bindTooltip(label, {
          permanent: true,
          direction: 'center',
          className: 'channel-map-country-label',
        });
      } else {
        layer.bindPopup(`<strong>${countryName}</strong><div class="text-muted small">Sin canales</div>`);
      }

      layer.on({
        mouseover: (event) => {
          event.target.setStyle({
            weight: 2,
            color: '#94a3b8',
            fillOpacity: 0.98,
          });
        },
        mouseout: (event) => {
          geoJsonRef.current?.resetStyle(event.target);
        },
      });
    },
    [channelCounts, goldCounts, channelsByCca3, showGold, onChannelClick]
  );

  return (
    <GeoJSON
      key={countsKey}
      ref={geoJsonRef}
      data={geoJson}
      style={style}
      onEachFeature={onEachFeature}
    />
  );
}

function FitMapToChannels({ countryIndex, channelCounts }) {
  const map = useMap();

  useEffect(() => {
    const activeCodes = Object.keys(channelCounts).filter((code) => channelCounts[code] > 0);
    if (!activeCodes.length || !countryIndex) {
      map.setView([20, -30], 2);
      return;
    }

    const points = activeCodes
      .map((code) => countryIndex.byCca3[code])
      .filter(Boolean)
      .map((entry) => [entry.lat, entry.lng]);

    if (!points.length) {
      map.setView([20, -30], 2);
      return;
    }

    if (points.length === 1) {
      map.setView(points[0], 4);
      return;
    }

    map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: 5 });
  }, [map, countryIndex, channelCounts]);

  return null;
}

function MapLegend({ maxCount }) {
  if (maxCount <= 0) return null;

  return (
    <div className="channels-world-map-legend" aria-hidden="true">
      <span className="channels-world-map-legend__label">Menos</span>
      <div className="channels-world-map-legend__bar" />
      <span className="channels-world-map-legend__label">Más</span>
      <span className="channels-world-map-legend__max">{maxCount} max</span>
    </div>
  );
}

function ChannelsWorldMap({ channels, usrType, onChannelClick }) {
  const [countryIndex, setCountryIndex] = useState(null);
  const [worldGeoJson, setWorldGeoJson] = useState(null);
  const [loadError, setLoadError] = useState('');
  const showGold = parseInt(usrType, 10) === 0;

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      loadCountriesData(),
      fetch(WORLD_GEOJSON_URL).then((response) => {
        if (!response.ok) throw new Error('No se pudo cargar el mapa de países');
        return response.json();
      }),
    ])
      .then(([countries, geoJson]) => {
        if (cancelled) return;
        setCountryIndex(buildCountryIndex(countries));
        setWorldGeoJson(geoJson);
        setLoadError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err.message || 'Error cargando el mapa');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const { counts, goldCounts, channelsByCca3, unlocated, maxCount } = useMemo(() => {
    if (!countryIndex) {
      return { counts: {}, goldCounts: {}, channelsByCca3: {}, unlocated: channels, maxCount: 0 };
    }
    return buildChannelStats(channels, countryIndex);
  }, [channels, countryIndex]);

  if (loadError) {
    return <div className="alert alert-warning mb-0">{loadError}</div>;
  }

  if (!countryIndex || !worldGeoJson) {
    return <div className="channels-world-map-loading text-muted py-4 text-center">Cargando mapa…</div>;
  }

  return (
    <div className="channels-world-map-root">
      <MapContainer
        className="channels-world-map"
        center={[20, -30]}
        zoom={2}
        scrollWheelZoom
        worldCopyJump
        zoomControl
        attributionControl={false}
      >
        <CountriesGeoJsonLayer
          geoJson={worldGeoJson}
          channelCounts={counts}
          goldCounts={goldCounts}
          channelsByCca3={channelsByCca3}
          maxCount={maxCount}
          showGold={showGold}
          onChannelClick={onChannelClick}
        />
        <FitMapToChannels countryIndex={countryIndex} channelCounts={counts} />
      </MapContainer>

      <MapLegend maxCount={maxCount} />

      {unlocated.length > 0 && (
        <div className="channels-world-map-unlocated mt-3">
          <div className="small fw-semibold mb-1">Sin ubicación en mapa ({unlocated.length})</div>
          <ul className="list-unstyled small mb-0 channels-world-map-unlocated-list">
            {unlocated.map((ch) => (
              <li key={ch.id}>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-start"
                  onClick={() => onChannelClick?.(ch.id)}
                >
                  {showGold && isGoldChannel(ch) && (
                    <span className="channel-gold-medal channel-gold-medal--inline me-1" aria-hidden="true" />
                  )}
                  {ch.name}
                  {ch.country ? ` (${ch.country})` : ''}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ChannelsWorldMap;
